import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Button,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  Alert,
  IconButton,
  Badge,
} from '@mui/material';
import { MdCheckCircle, MdCancel, MdAdminPanelSettings, MdPeople, MdHourglassEmpty, MdArrowBack } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEOMeta from '../components/SEOMeta/SEOMeta';

const ROLES = ['admin', 'teacher', 'student', 'other'];

const roleChipColors = {
  admin: { bg: '#b71c1c', text: '#ffcdd2' },
  teacher: { bg: '#1a237e', text: '#c5cae9' },
  student: { bg: '#1b5e20', text: '#c8e6c9' },
  other: { bg: '#3e2723', text: '#d7ccc8' },
};

const statusChipColors = {
  active: { bg: '#1b5e20', text: '#a5d6a7' },
  pending: { bg: '#e65100', text: '#ffcc80' },
};

const RoleChip = ({ role }) => {
  const colors = roleChipColors[role] || { bg: '#333', text: '#ccc' };
  return (
    <Chip
      label={role}
      size="small"
      sx={{
        background: colors.bg,
        color: colors.text,
        fontWeight: 700,
        fontSize: '0.7rem',
        textTransform: 'capitalize',
        borderRadius: 1,
        px: 0.5,
      }}
    />
  );
};

const getInitials = (name) => name ? name.slice(0, 2).toUpperCase() : 'U';

const avatarBg = (role) => {
  const map = { admin: '#b71c1c', teacher: '#1a237e', student: '#1b5e20', other: '#4e342e' };
  return map[role] || '#555';
};

const AdminPanelPage = () => {
  const { currentUser, isAdmin, pendingUsers, users, approveUser, rejectUser, updateUserRole } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [pendingRoles, setPendingRoles] = useState({});
  const [activeRoles, setActiveRoles] = useState({});
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  if (!isAdmin) {
    return (
      <Box sx={pageWrap}>
        <Alert severity="error" sx={{ maxWidth: 480, mx: 'auto', mt: 8, borderRadius: 3 }}>
          Access denied. Admin privileges required.
        </Alert>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate('/')} startIcon={<MdArrowBack />} sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.2)' }}>
            Back to Home
          </Button>
        </Box>
      </Box>
    );
  }

  const activeUsers = users.filter((u) => u.id !== currentUser.id); // Exclude self from list

  const handleApprove = (pendingId) => {
    const role = pendingRoles[pendingId] || 'student';
    approveUser(pendingId, role);
    showToast(`User approved with role: ${role}`);
  };

  const handleReject = (pendingId) => {
    rejectUser(pendingId);
    showToast('User request rejected.');
  };

  const handleRoleUpdate = (userId) => {
    const newRole = activeRoles[userId];
    if (!newRole) return;
    updateUserRole(userId, newRole);
    showToast(`Role updated to: ${newRole}`);
    setActiveRoles((prev) => { const n = { ...prev }; delete n[userId]; return n; });
  };

  return (
    <Box sx={pageWrap}>
      <SEOMeta
        title="Admin Dashboard | Algodoor Training Management"
        description="Manage students, courses, enrollments, and certificates from the admin dashboard."
        keywords="admin dashboard, student management, course management, training platform"
        url="/admin"
        type="WebPage"
        noindex
      />
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 1, sm: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>
            <MdArrowBack />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ background: 'linear-gradient(135deg,#b71c1c,#e53935)', width: 48, height: 48, boxShadow: '0 4px 20px rgba(183,28,28,0.4)' }}>
              <MdAdminPanelSettings style={{ fontSize: 26, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={800} color="white" letterSpacing={0.3}>
                Admin Panel
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.45)">
                Logged in as <strong style={{ color: '#ffb347' }}>{currentUser.username}</strong>
              </Typography>
            </Box>
          </Box>
        </Box>

        {toast && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2, background: 'rgba(46,125,50,0.2)', border: '1px solid rgba(46,125,50,0.4)', color: '#a5d6a7', '& .MuiAlert-icon': { color: '#a5d6a7' } }}>
            {toast}
          </Alert>
        )}

        {/* Stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
          {[
            { icon: <MdPeople style={{ color: 'white' }} />, label: 'Total Users', value: users.length, color: '#1565c0' },
            { icon: <MdHourglassEmpty style={{ color: 'white' }} />, label: 'Pending Requests', value: pendingUsers.length, color: '#e65100', pulse: pendingUsers.length > 0 },
            { icon: <MdCheckCircle style={{ color: 'white' }} />, label: 'Active Users', value: users.filter((u) => u.status === 'active').length, color: '#2e7d32' },
          ].map((stat) => (
            <Card key={stat.label} elevation={0} sx={statCardStyle(stat.color)}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Avatar sx={{ background: stat.color, width: 44, height: 44 }}>{stat.icon}</Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={800} color="white">{stat.value}</Typography>
                  <Typography variant="caption" color="rgba(255,255,255,0.5)" fontSize="0.75rem">{stat.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 3,
            '& .MuiTab-root': { color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' },
            '& .Mui-selected': { color: 'white !important' },
            '& .MuiTabs-indicator': { background: 'linear-gradient(90deg,#AD1C00,#ff4444)', height: 3, borderRadius: 3 },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MdHourglassEmpty style={{ fontSize: 18 }} />
                Pending Requests
                {pendingUsers.length > 0 && (
                  <Badge badgeContent={pendingUsers.length} color="error" sx={{ ml: 0.5 }} />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MdPeople style={{ fontSize: 18 }} />
                Active Users
              </Box>
            }
          />
        </Tabs>

        {/* Pending Users Tab */}
        {tab === 0 && (
          <Card elevation={0} sx={tableCardStyle}>
            {pendingUsers.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <MdHourglassEmpty style={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', marginBottom: 16 }} />
                <Typography color="rgba(255,255,255,0.4)" variant="body1">
                  No pending signup requests
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {['User', 'Email', 'Requested', 'Assign Role', 'Actions'].map((h) => (
                        <TableCell key={h} sx={thStyle}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingUsers.map((user) => (
                      <TableRow key={user.id} sx={trStyle}>
                        <TableCell sx={tdStyle}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 34, height: 34, background: '#e65100', fontSize: '0.75rem', fontWeight: 700 }}>
                              {getInitials(user.username)}
                            </Avatar>
                            <Typography variant="body2" color="white" fontWeight={600}>{user.username}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={tdStyle}>
                          <Typography variant="body2" color="rgba(255,255,255,0.65)" fontSize="0.82rem">{user.email}</Typography>
                        </TableCell>
                        <TableCell sx={tdStyle}>
                          <Typography variant="caption" color="rgba(255,255,255,0.4)">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tdStyle}>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={pendingRoles[user.id] || 'student'}
                              onChange={(e) => setPendingRoles((prev) => ({ ...prev, [user.id]: e.target.value }))}
                              sx={selectStyle}
                            >
                              {ROLES.map((r) => (
                                <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell sx={tdStyle}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Approve">
                              <IconButton
                                onClick={() => handleApprove(user.id)}
                                sx={{ color: '#66bb6a', '&:hover': { background: 'rgba(102,187,106,0.12)' } }}
                              >
                                <MdCheckCircle />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                onClick={() => handleReject(user.id)}
                                sx={{ color: '#ef5350', '&:hover': { background: 'rgba(239,83,80,0.12)' } }}
                              >
                                <MdCancel />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        )}

        {/* Active Users Tab */}
        {tab === 1 && (
          <Card elevation={0} sx={tableCardStyle}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['User', 'Email', 'Current Role', 'Status', 'Update Role', 'Action'].map((h) => (
                      <TableCell key={h} sx={thStyle}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} sx={trStyle}>
                      <TableCell sx={tdStyle}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 34, height: 34, background: avatarBg(user.role), fontSize: '0.75rem', fontWeight: 700 }}>
                            {getInitials(user.username)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="white" fontWeight={600}>{user.username}</Typography>
                            {user.id === currentUser.id && (
                              <Typography variant="caption" color="#ffb347" fontSize="0.68rem">You</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={tdStyle}>
                        <Typography variant="body2" color="rgba(255,255,255,0.65)" fontSize="0.82rem">{user.email}</Typography>
                      </TableCell>
                      <TableCell sx={tdStyle}>
                        <RoleChip role={user.role} />
                      </TableCell>
                      <TableCell sx={tdStyle}>
                        <Chip
                          label={user.status}
                          size="small"
                          sx={{
                            background: statusChipColors[user.status]?.bg || '#333',
                            color: statusChipColors[user.status]?.text || '#ccc',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            textTransform: 'capitalize',
                            borderRadius: 1,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={tdStyle}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={activeRoles[user.id] ?? user.role}
                            onChange={(e) => setActiveRoles((prev) => ({ ...prev, [user.id]: e.target.value }))}
                            sx={selectStyle}
                          >
                            {ROLES.map((r) => (
                              <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell sx={tdStyle}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!activeRoles[user.id] || activeRoles[user.id] === user.role}
                          onClick={() => handleRoleUpdate(user.id)}
                          sx={{
                            background: 'linear-gradient(135deg,#1565c0,#1976d2)',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 1.5,
                            fontSize: '0.78rem',
                            px: 2,
                            '&:disabled': { opacity: 0.35 },
                          }}
                        >
                          Update
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Box>
    </Box>
  );
};

// Styles
const pageWrap = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0b0c10 0%, #1a1b2e 50%, #16213e 100%)',
  py: { xs: 3, sm: 5 },
  px: { xs: 1, sm: 2 },
};

const statCardStyle = (color) => ({
  background: `rgba(255,255,255,0.04)`,
  border: `1px solid ${color}33`,
  borderRadius: 3,
  backdropFilter: 'blur(10px)',
  transition: 'transform 0.2s',
  '&:hover': { transform: 'translateY(-2px)' },
});

const tableCardStyle = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 3,
  backdropFilter: 'blur(10px)',
  overflow: 'hidden',
};

const thStyle = {
  color: 'rgba(255,255,255,0.45)',
  fontWeight: 700,
  fontSize: '0.78rem',
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  borderBottom: '1px solid rgba(255,255,255,0.07)',
  py: 1.8,
  background: 'rgba(255,255,255,0.02)',
};

const tdStyle = {
  color: 'rgba(255,255,255,0.8)',
  borderBottom: '1px solid rgba(255,255,255,0.05)',
  py: 1.5,
};

const trStyle = {
  transition: 'background 0.15s',
  '&:hover': { background: 'rgba(255,255,255,0.03)' },
  '&:last-child td': { borderBottom: 'none' },
};

const selectStyle = {
  color: 'white',
  fontSize: '0.82rem',
  borderRadius: 1.5,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1565c0' },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
  background: 'rgba(255,255,255,0.05)',
  textTransform: 'capitalize',
};

export default AdminPanelPage;
